import { Body, Controller, Delete, Get, Param, Patch, Post, Req, UseGuards } from '@nestjs/common';
import {
  ApiBearerAuth,
  ApiBody,
  ApiOperation,
  ApiParam,
  ApiResponse,
  ApiSecurity,
  ApiTags,
} from '@nestjs/swagger';
import { AddCartItemRequest, ApplyCouponRequest, CartSummary, UpdateCartItemRequest } from '@cart/shared-types';
import { GuardedRequest, AuthGuard } from '../auth/auth.guard';
import { CartService } from './cart.service';

@ApiTags('Cart')
@ApiBearerAuth()
@ApiSecurity('store-id')
@UseGuards(AuthGuard)
@Controller('cart')
export class CartController {
  constructor(private readonly cartService: CartService) {}

  @Get()
  @ApiOperation({ summary: 'Get the current user cart' })
  @ApiResponse({ status: 200, description: 'Returns cart with items and subtotal' })
  getCart(@Req() request: GuardedRequest): Promise<CartSummary> {
    return this.cartService.getCart(request.user.id);
  }

  @Post('items')
  @ApiOperation({ summary: 'Add a product to the cart' })
  @ApiBody({
    schema: {
      type: 'object',
      required: ['productId', 'quantity'],
      properties: {
        productId: { type: 'string', example: 'clx1234abcd' },
        quantity: { type: 'integer', minimum: 1, example: 2 },
      },
    },
  })
  @ApiResponse({ status: 201, description: 'Item added — returns updated cart' })
  @ApiResponse({ status: 400, description: 'Quantity ≤ 0 or exceeds available stock' })
  addItem(
    @Req() request: GuardedRequest,
    @Body() body: AddCartItemRequest
  ): Promise<CartSummary> {
    return this.cartService.addItem(request.user.id, body);
  }

  @Patch('items/:id')
  @ApiOperation({ summary: 'Update quantity of a cart item' })
  @ApiParam({ name: 'id', description: 'Cart item id' })
  @ApiBody({
    schema: {
      type: 'object',
      required: ['quantity'],
      properties: {
        quantity: { type: 'integer', minimum: 1, example: 3 },
      },
    },
  })
  @ApiResponse({ status: 200, description: 'Quantity updated — returns updated cart' })
  @ApiResponse({ status: 404, description: 'Cart item not found' })
  updateItem(
    @Req() request: GuardedRequest,
    @Param('id') itemId: string,
    @Body() body: UpdateCartItemRequest
  ): Promise<CartSummary> {
    return this.cartService.updateItem(request.user.id, itemId, body);
  }

  @Delete('items/:id')
  @ApiOperation({ summary: 'Remove an item from the cart' })
  @ApiParam({ name: 'id', description: 'Cart item id' })
  @ApiResponse({ status: 200, description: 'Item removed — returns updated cart' })
  @ApiResponse({ status: 404, description: 'Cart item not found' })
  removeItem(
    @Req() request: GuardedRequest,
    @Param('id') itemId: string
  ): Promise<CartSummary> {
    return this.cartService.removeItem(request.user.id, itemId);
  }

  @Delete()
  @ApiOperation({ summary: 'Clear all items from the cart' })
  @ApiResponse({ status: 200, description: 'Cart cleared — returns empty cart' })
  clear(@Req() request: GuardedRequest): Promise<CartSummary> {
    return this.cartService.clear(request.user.id);
  }

  @Post('coupon')
  @ApiOperation({ summary: 'Apply a discount coupon code to the cart' })
  @ApiBody({
    schema: {
      type: 'object',
      required: ['code'],
      properties: { code: { type: 'string', example: 'WELCOME10' } },
    },
  })
  @ApiResponse({ status: 200, description: 'Coupon applied — returns updated cart with discount' })
  @ApiResponse({ status: 400, description: 'Coupon does not meet minimum order or is no longer valid' })
  @ApiResponse({ status: 404, description: 'Coupon code not found' })
  applyCoupon(
    @Req() request: GuardedRequest,
    @Body() body: ApplyCouponRequest
  ): Promise<CartSummary> {
    return this.cartService.applyCoupon(request.user.id, body.code);
  }

  @Delete('coupon')
  @ApiOperation({ summary: 'Remove the applied coupon from the cart' })
  @ApiResponse({ status: 200, description: 'Coupon removed — returns updated cart' })
  removeCoupon(@Req() request: GuardedRequest): Promise<CartSummary> {
    return this.cartService.removeCoupon(request.user.id);
  }
}
